import { useMemo } from 'react';
import { assert, keyBy } from '@varos/util-typescript';
import { ReportingControllerBaseConfig } from '../../../base';
import { CollectionController } from './collectionInterface';
import { CollectionViewProps } from './collectionProps';
import { DatabaseTableViewProps } from '../../../view/database-view/table';
import {
    DatabaseListItemPropertyProps,
    DatabaseListItemProps,
    DatabaseListViewProps,
} from '../../../view/database-view/list';
import {
    HiDocument,
    HiDotsHorizontal,
    HiOutlineDocument,
    HiOutlineDocumentText,
    HiOutlineFilter,
    HiOutlineRefresh,
    HiOutlineTrash,
} from 'react-icons/hi';
import { AiOutlineSearch, AiOutlineFilter } from 'react-icons/ai';
import pluralize from 'pluralize';
import { HiOutlineListBullet } from 'react-icons/hi2';
import { DatabaseFilterContainerProps } from '../../../view/database-view/filter';
import { Property, ContentObject } from '@varos/research-sdk-api';
import { CollectionItemAggregate, CollectionItemContainerProps } from './item';

export function createCollectionController(
    config: ReportingControllerBaseConfig
): CollectionController {
    const {
        kernel: {
            infra: { formatter },
        },
    } = config;

    function truncate(value: string, maxLength: number = 30) {
        return value;
        // if (value.length <= maxLength) {
        //     return value;
        // }
        // return value.slice(0, maxLength) + '...';
    }

    return {
        useProps(context, item, props): CollectionViewProps {
            const table = useMemo((): DatabaseTableViewProps => {
                return {
                    columns: item.collection.properties.map((property: Property) => ({
                        key: property.key,
                        label: property.display_name,
                    })),
                    rows: item.record.items.map((contentItem: ContentObject) => ({
                        id: contentItem.id,
                        data: contentItem.properties,
                    })),
                };
            }, [item.record.items]);

            const propertiesByKey = useMemo(
                () =>
                    keyBy(
                        item.collection.properties,
                        (property: Property) => property.key
                    ),
                [item.collection.properties]
            );

            const list = useMemo((): DatabaseListViewProps => {
                return {
                    items: item.record.items.map(
                        (content: ContentObject): DatabaseListItemProps => ({
                            id: content.id,
                            label: content.title ?? 'Untitled',
                            icon: content.icon
                                ? {
                                      type: 'emoji',
                                      emoji: content.icon,
                                  }
                                : {
                                      type: 'icon',
                                      Component: HiOutlineDocumentText,
                                  },
                            open: {
                                button: {
                                    onClick(event) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        props.record.onOpen(content);
                                    },
                                },
                            },
                            properties: item.collection.properties.map(
                                (property: Property): DatabaseListItemPropertyProps => {
                                    const value =
                                        content.properties[property.key] ?? null;
                                    return {
                                        id: property.key,
                                        property: {
                                            label: property.display_name,
                                            description: property.description,
                                        },
                                        label: value ? truncate(value.toString()) : `N/A`,
                                        tooltip: {
                                            label: property.description,
                                        },
                                        value: {
                                            item: {
                                                property,
                                                value:
                                                    content.properties[property.key] ??
                                                    null,
                                            },
                                            onChange(value) {
                                                throw new Error(
                                                    'onChange handler not implemented'
                                                );
                                            },
                                        },
                                    };
                                }
                            ),
                        })
                    ),
                };
            }, [item.record.items, item.collection]);

            const lastUpdatedRelative = useMemo(
                () =>
                    formatter
                        .timeago(new Date(item.record.query.dataUpdatedAt))
                        .toLowerCase(),
                [item.record.query.dataUpdatedAt]
            );

            const pluralized = pluralize('item', item.count.value);

            const propertyByKey = useMemo(
                () =>
                    keyBy(
                        item.collection.properties,
                        (property: Property) => property.key
                    ),
                [item.collection.properties]
            );

            const items = useMemo(() => {
                return Object.entries(props.filter.value).map(([key, value]) => {
                    const property = propertyByKey[key];
                    assert(property, `Property ${key} not found`);
                    return { property, value };
                });
            }, [props.filter.value]);

            const list2 = useMemo(() => {
                return {
                    items: item.record.items.map(
                        (
                            content
                        ): Omit<CollectionItemContainerProps, 'as'> & {
                            id: string;
                        } => ({
                            id: content.id,
                            item: {
                                collection: item.collection,
                                document: content,
                            },
                        })
                    ),
                };
            }, [item.record.items, item.collection, item.record.query.dataUpdatedAt]);

            return {
                table,
                list,
                list2,
                new: {
                    label: 'New',
                    button: {
                        onClick() {
                            props.onNewClick();
                        },
                    },
                },
                filter: {
                    isVisible: props.filter.visible.value,
                    suggestions: item.collection.properties
                        .filter((candidate: Property) => candidate.type === 'relation')
                        .map(
                            (
                                property: Property
                            ): Omit<DatabaseFilterContainerProps, 'as'> => ({
                                property: property,
                                value: props.filter.value[property.key] ?? null,
                                onChange(value) {
                                    return props.filter.onAdd(property, value);
                                },
                            })
                        ),
                    items: items.map((filterItem) => ({
                        id: filterItem.property.key,
                        label: `${filterItem.property.display_name} ${filterItem.value}`,
                        remove: {
                            button: {
                                onClick() {
                                    props.filter.onRemove(filterItem.property);
                                },
                            },
                        },
                    })),
                },
                collection: {
                    total: {
                        label: `${item.count.value} ${pluralized}`,
                        value: item.count.value,
                    },
                },
                status: {
                    isLoading: item.record.query.isRefetching,
                    lastUpdated: {
                        label: `Data refreshed ${lastUpdatedRelative}`,
                    },
                },
                menu: {
                    Icon: HiDotsHorizontal,
                    tooltip: {
                        label: 'Actions',
                    },
                    items: [
                        {
                            id: 'refresh',
                            label: 'Refresh data',
                            Icon: HiOutlineRefresh,
                            button: {
                                onClick() {
                                    return item.record.query.refetch();
                                },
                            },
                            colorScheme: null,
                        },
                    ],
                },
                layout: {
                    button: {
                        label: 'List',
                        Icon: HiOutlineListBullet,
                    },
                },
                actions: {
                    items: [
                        {
                            id: 'filter',
                            label: 'Filter',
                            Icon: AiOutlineFilter,
                            button: {
                                onClick(event) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    console.log('toggling', props.filter.visible.value);
                                    props.filter.visible.onChange(
                                        !props.filter.visible.value
                                    );
                                },
                                isActive: props.filter.visible.value,
                            },
                        },
                        {
                            id: 'search',
                            label: 'Search',
                            Icon: AiOutlineSearch,
                            button: {
                                onClick(event) {
                                    event.preventDefault();
                                },
                                isActive: false,
                            },
                        },
                    ],
                },
            };
        },
    };
}
